import type {
  CheckboxProps as AriaCheckboxProps,
  CheckboxRenderProps,
} from 'react-aria-components';

import { useCallback, type ReactNode, type Ref } from 'react';
import { Checkbox as AriaCheckbox } from 'react-aria-components';

import { cx } from '@/utils/cx';

export interface CheckboxBaseProps {
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  isInvalid?: boolean;
}

export const CheckboxBase = ({
  className,
  isSelected,
  isDisabled,
  isIndeterminate,
  isInvalid = false,
  size = 'sm',
  isFocusVisible = false,
}: CheckboxBaseProps) => {
  return (
    <div
      className={cx(
        'relative flex size-4 shrink-0 cursor-pointer appearance-none items-center justify-center rounded bg-primary ring-1 ring-inset',
        isInvalid ? 'ring-error_subtle' : 'ring-primary',
        size === 'md' && 'size-5 rounded-md',
        (isSelected || isIndeterminate) && 'bg-brand-solid ring-bg-brand-solid',
        isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
        isInvalid && isFocusVisible && 'ring-2 ring-error',
        !isInvalid &&
          isFocusVisible &&
          'outline-2 outline-offset-2 outline-focus-ring',
        className,
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 14 14"
        fill="none"
        className={cx(
          'pointer-events-none absolute h-3 w-2.5 text-fg-white opacity-0 transition-inherit-all',
          size === 'md' && 'size-3.5',
          isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle',
        )}
      >
        <path
          d="M2.91675 7H11.0834"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <svg
        aria-hidden="true"
        viewBox="0 0 14 14"
        fill="none"
        className={cx(
          'pointer-events-none absolute size-3 text-fg-white opacity-0 transition-inherit-all',
          size === 'md' && 'size-3.5',
          isSelected && !isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle',
        )}
      >
        <path
          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};
CheckboxBase.displayName = 'CheckboxBase';

const sizes = {
  xs: {
    root: 'gap-1',
    textWrapper: '',
    label: 'text-xs font-medium',
    hint: 'text-xs',
  },

  sm: {
    root: 'gap-2',
    textWrapper: '',
    label: 'text-sm font-medium',
    hint: 'text-sm',
  },
  md: {
    root: 'gap-3',
    textWrapper: 'gap-0.5',
    label: 'text-md font-medium',
    hint: 'text-md',
  },
};

interface CheckboxProps extends AriaCheckboxProps {
  ref?: Ref<HTMLLabelElement>;
  size?: 'xs' | 'sm' | 'md';
  label?: ReactNode;
  hint?: ReactNode;
  /** Applied to the hint wrapper for `aria-describedby` / `aria-errormessage`. */
  hintId?: string;
}

export const Checkbox = ({
  label,
  hint,
  hintId,
  size = 'sm',
  className,
  ...ariaCheckboxProps
}: CheckboxProps) => {
  const checkboxClassNameFromState: CheckboxProps['className'] = useCallback(
    (state: CheckboxRenderProps & { defaultClassName: string | undefined }) => {
      return cx(
        'flex items-start',
        state.isDisabled && 'cursor-not-allowed',
        sizes[size].root,
        typeof className === 'function' ? className(state) : className,
      );
    },
    [size, className],
  );

  return (
    <AriaCheckbox {...ariaCheckboxProps} className={checkboxClassNameFromState}>
      {({
        isSelected,
        isIndeterminate,
        isDisabled,
        isFocusVisible,
        isInvalid,
      }) => (
        <>
          <CheckboxBase
            size={size}
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            isFocusVisible={isFocusVisible}
            className={label || hint ? 'mt-0.5' : ''}
          />
          {(label || hint) && (
            <div
              className={cx('inline-flex flex-col', sizes[size].textWrapper)}
            >
              {label && (
                <p
                  className={cx(
                    'text-secondary select-none',
                    sizes[size].label,
                  )}
                >
                  {label}
                </p>
              )}
              {hint && (
                // Lint rules don't apply because this isn't actually interactive.
                // oxlint-disable-next-line jsx_a11y/click-events-have-key-events, jsx_a11y/no-static-element-interactions
                <span
                  id={hintId}
                  className={cx('text-tertiary', sizes[size].hint)}
                  onClick={stopPropagation}
                >
                  {hint}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </AriaCheckbox>
  );
};
Checkbox.displayName = 'Checkbox';

function stopPropagation<T extends React.SyntheticEvent>(event: T) {
  event.stopPropagation();
}
